import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Footer from '../components/Footer';
import PublicHeader from '../components/PublicHeader';
import { Seo, faqJsonLd } from '../components/Seo';
import { 
  ArrowRight,
  FileText,
  Users,
  CreditCard,
  Shield,
  Zap,
  BarChart3,
  Mail,
  Repeat,
  Clock,
  CheckCircle2,
  Lock,
  Globe,
  Megaphone,
  Package,
  MessageCircle
} from 'lucide-react';


const ABOUT_FAQS = [
  {
    q: 'Do I need a Stripe account?',
    a: "Yes, to accept payments you'll need to connect a Stripe account. Don't worry\u2014it's free to create and takes just a few minutes. We'll guide you through the process in your Settings page.",
  },
  {
    q: 'What fees does CreateCollect charge?',
    a: "For card payments through invoices, a processing fee of 3.4% + $0.30 per transaction is deducted from your payout. This covers Stripe's fees plus platform costs. Your monthly subscription covers all other features.",
  },
  {
    q: 'Can I try before I buy?',
    a: "Absolutely! Our Free plan lets you send 2 invoices per month with no time limit. It's a great way to test the platform before upgrading.",
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes, you can cancel or change your plan at any time. If you downgrade, you'll keep your current plan's features until the end of your billing period.",
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted in transit and at rest. Payment processing is handled entirely by Stripe\u2014we never see or store credit card information.',
  },
  {
    q: 'Can I use CreateCollect internationally?',
    a: 'Yes! CreateCollect supports multiple currencies and Stripe is available in 40+ countries. You can invoice clients anywhere in the world.',
  },
  {
    q: 'What kind of support do you offer?',
    a: 'Standard and Pro subscribers get instant AI support chat right inside the app, any time \u2014 it knows the product inside and out. Everyone, on any plan, can email support@createcollect.com and a human will get back to you, usually within one business day.',
  },
];

const About = () => {

  const howItWorks = [
    {
      step: '1',
      title: 'Create Your Account',
      description: 'Sign up in seconds with just your email and password. No credit card required to start.'
    },
    {
      step: '2',
      title: 'Add Your Clients',
      description: 'Build your client database with names, emails, and contact details for quick invoicing.'
    },
    {
      step: '3',
      title: 'Create & Send Invoices',
      description: 'Design professional invoices with your branding, add line items, and send directly to clients.'
    },
    {
      step: '4',
      title: 'Get Paid',
      description: 'Clients pay securely via Stripe. Funds go directly to your connected bank account.'
    }
  ];

  const paymentFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Secure Payments',
      description: 'All payments are processed through Stripe, a PCI-DSS Level 1 certified payment processor trusted by millions of businesses worldwide.'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Direct Deposits',
      description: 'Connect your own Stripe account and receive payments directly to your bank. No middleman, no delays.'
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Encrypted Data',
      description: 'Your financial data and client information are encrypted and securely stored. We never see your card details.'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Multi-Currency',
      description: 'Accept payments in multiple currencies. Bill international clients in their local currency.'
    }
  ];

  const tiers = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for freelancers just getting started',
      color: 'bg-slate-100',
      features: [
        { name: '2 invoices per month', included: true },
        { name: 'Brutalist & Classic templates', included: true },
        { name: 'Client management', included: true },
        { name: 'Basic dashboard', included: true },
        { name: 'Email invoices', included: true },
        { name: 'PDF downloads', included: true },
        { name: 'Online payments via Stripe', included: true },
        { name: '&quot;Powered by CreateCollect&quot; branding', included: true, note: 'on invoices' },
        { name: 'Multiple templates', included: false },
        { name: 'Business reports', included: false },
        { name: 'Marketing campaigns', included: false },
        { name: 'Recurring invoices', included: false },
        { name: 'AI support chat', included: false }
      ]
    },
    {
      name: 'Standard',
      price: '$5',
      period: '/month',
      description: 'For growing creative businesses',
      color: 'bg-primary/10',
      popular: true,
      features: [
        { name: '5 invoices per month', included: true },
        { name: 'All 5 invoice templates', included: true, note: 'Brutalist, Classic, Modern, Minimal, Aesthetic' },
        { name: 'Client management', included: true },
        { name: 'Business reports & analytics', included: true },
        { name: 'Remove CreateCollect branding', included: true },
        { name: 'Custom invoice prefix', included: true, note: 'e.g., STUDIO-001' },
        { name: 'Multi-currency support', included: true },
        { name: 'Tax calculation', included: true },
        { name: 'Partial payments', included: true },
        { name: 'CSV export', included: true },
        { name: 'Upload your logo', included: true },
        { name: 'Marketing campaigns', included: true, note: '500 emails/month' },
        { name: 'AI support chat', included: true },
        { name: 'Recurring invoices', included: false },
        { name: 'Scheduled sending', included: false }
      ]
    },
    {
      name: 'Pro',
      price: '$8',
      period: '/month',
      description: 'Unlimited power for serious professionals',
      color: 'bg-amber-100',
      features: [
        { name: 'Unlimited invoices', included: true },
        { name: 'Everything in Standard', included: true },
        { name: 'Scheduled invoice sending', included: true },
        { name: 'Recurring invoices', included: true, note: 'Weekly, monthly, yearly' },
        { name: 'Automatic payment reminders', included: true },
        { name: 'Duplicate invoices', included: true },
        { name: 'Advanced analytics', included: true },
        { name: 'Marketing campaigns', included: true, note: '2,000 emails/month' },
        { name: 'AI support chat', included: true },
        { name: 'Priority support', included: true },
        { name: 'Early access to new features', included: true }
      ]
    }
  ];

  const allFeatures = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Professional Invoices',
      description: 'Choose from 5 beautifully designed templates: Brutalist, Classic, Modern, Minimal, and Aesthetic. Each template is optimized for different industries and styles.',
      tier: 'All plans'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Client Management',
      description: 'Store client details, view invoice history, and manage relationships all in one place. Quick-select clients when creating new invoices.',
      tier: 'All plans'
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Product Catalog',
      description: 'Save frequently used items and services. Add them to invoices with one click, complete with descriptions and pricing.',
      tier: 'All plans'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Stripe Connect',
      description: 'Accept credit card payments directly. Clients pay through a secure link, and funds are deposited straight to your bank account.',
      tier: 'All plans'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Reports & Analytics',
      description: 'Track revenue, view payment trends, identify top clients, and monitor your business growth with detailed reports.',
      tier: 'Standard & Pro'
    },
    {
      icon: <Repeat className="w-8 h-8" />,
      title: 'Recurring Invoices',
      description: 'Set up automatic invoicing for retainer clients. Choose weekly, monthly, or yearly billing cycles.',
      tier: 'Pro only'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Scheduled Sending',
      description: 'Write invoices now, send them later. Perfect for billing at the end of projects or on specific dates.',
      tier: 'Pro only'
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: 'Payment Reminders',
      description: 'Automatic email reminders for overdue invoices. Get paid faster without awkward follow-ups.',
      tier: 'Pro only'
    },
    {
      icon: <Megaphone className="w-8 h-8" />,
      title: 'Marketing Campaigns',
      description: 'Design emails with a drag-and-drop builder, send to your client list, and track opens and clicks. Announce new services, promote seasonal offers, and win repeat business — without paying for a separate email tool.',
      tier: 'Standard & Pro'
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: 'AI Support Chat',
      description: 'Instant answers about invoices, payments, and campaigns, built into the app for subscribers.',
      tier: 'Standard & Pro'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="How CreateCollect Works — Features, Plans & Pricing"
        description="See how CreateCollect handles invoices, card payments, and client email campaigns — plus what's included on the Free, Standard, and Pro plans."
        path="/about"
        jsonLd={faqJsonLd(ABOUT_FAQS)}
      />
      {/* Header */}
      <PublicHeader secondaryLabel="Home" secondaryHref="/" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6">
            The Complete Guide to <span className="text-primary">CreateCollect</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Everything you need to know about how CreateCollect helps freelancers and creatives 
            get paid faster with beautiful, professional invoices.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-heading font-bold text-center mb-4">How It Works</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            From sign-up to getting paid, here&apos;s the simple process that helps creatives spend less time on paperwork.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-6 -right-4 w-8 h-8 text-muted-foreground/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Processing */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-heading font-bold text-center mb-4">How Payments Work</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We use Stripe Connect to ensure your payments are secure, fast, and go directly to you.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {paymentFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-muted/50 rounded-2xl p-8">
            <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              The Payment Flow
            </h3>
            <ol className="space-y-4">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-medium">1</span>
                <div>
                  <strong>You send an invoice</strong> — Your client receives a professional email with a secure payment link.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-medium">2</span>
                <div>
                  <strong>Client clicks &quot;Pay Now&quot;</strong> — They&apos;re taken to a secure Stripe checkout page with your invoice details.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-medium">3</span>
                <div>
                  <strong>Payment is processed</strong> — Stripe securely handles the transaction. We never see card details.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-medium">4</span>
                <div>
                  <strong>Funds hit your account</strong> — Money goes directly to your connected Stripe account (typically 2 business days).
                </div>
              </li>
            </ol>
            <p className="mt-6 text-sm text-muted-foreground">
              <strong>Note:</strong> A processing fee of 3.4% + $0.30 per transaction is deducted from your payout — this covers Stripe&apos;s card processing costs plus platform costs. There are no other transaction fees, and your clients always pay the exact invoice amount.
            </p>
          </div>
        </div>
      </section>

      {/* All Features */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-heading font-bold text-center mb-4">Features In-Depth</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            A closer look at everything CreateCollect offers to streamline your invoicing.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {allFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{feature.description}</p>
                  <span className="inline-block text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                    {feature.tier}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers Detailed */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-heading font-bold text-center mb-4">Plans & Pricing</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <Card key={index} className={`relative border-0 shadow-lg ${tier.popular ? 'ring-2 ring-primary' : ''}`}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardContent className="pt-8">
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${tier.color}`}>
                    {tier.name}
                  </div>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">{tier.price}</span>
                    <span className="text-muted-foreground">{tier.period}</span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-6">{tier.description}</p>
                  
                  <ul className="space-y-3">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        {feature.included ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-muted flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <span className={feature.included ? '' : 'text-muted-foreground'}>
                            {feature.name}
                          </span>
                          {feature.note && (
                            <span className="text-xs text-muted-foreground block">{feature.note}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/register" className="block mt-8">
                    <Button className="w-full rounded-full" variant={tier.popular ? 'default' : 'outline'}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8">
            Online card payments: 3.4% + $0.30 per transaction
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-heading font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {ABOUT_FAQS.map((faq, i) => (
              <div key={i} className="bg-background rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-lg mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-6">
            Ready to get paid for your creative work?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Spend less time on paperwork and more time on the work you love. Start invoicing today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="rounded-full gap-2 px-8">
                Create Free Account <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            No credit card required • 2 free invoices every month
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
